import { Typography } from '@mui/material';
import { format } from 'date-fns';
import {
  Fact,
  LedgerHoverCard,
  RefCode,
  StatusWord,
  formatLedgerWhenFull,
  percentOf,
  statusToken,
} from '../../../shared/components/ledger';
import { formatCurrency } from '../../../shared/utils/currency';

/**
 * The site ledger's fact list. Every fact is conditional because the screens
 * carry different data; this one needs no extra fetch — the allocation rows
 * already carry approval details and the parent item carries the target.
 */

export interface SiteWorkHoverCardProps {
  title: string;
  kind: string;
  description?: string;
  status?: string;
  reference?: string | null;
  who?: string;
  whenIso?: string;
  amount?: number | null;
  target?: number | null;
  approvedBy?: string | null;
  approvedAt?: string | null;
  rejectedBy?: string | null;
  rejectionReason?: string | null;
  showValues?: boolean;
  onOpenDetails?: () => void;
}

export function SiteWorkHoverCard({
  showValues = true,
  ...facts
}: SiteWorkHoverCardProps) {
  const amount = showValues ? facts.amount : null;
  const target = showValues ? facts.target : null;
  const percent =
    amount != null && target != null ? percentOf(amount, target) : null;

  return (
    <LedgerHoverCard
      title={facts.title}
      kind={facts.kind}
      description={facts.description}
      onOpenDetails={facts.onOpenDetails}
    >
      {facts.status && (
        <Fact label="Status">
          <StatusWord token={statusToken(facts.status)} />
        </Fact>
      )}
      {facts.approvedBy && (
        <Fact label="Approved by">
          {facts.approvedBy}
          {facts.approvedAt
            ? ` · ${format(new Date(facts.approvedAt), 'MMM d')}`
            : ''}
        </Fact>
      )}
      {facts.rejectedBy && <Fact label="Rejected by">{facts.rejectedBy}</Fact>}
      {facts.rejectionReason && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', py: 0.25 }}
        >
          “{facts.rejectionReason}”
        </Typography>
      )}
      {facts.reference && (
        <Fact label="Ref">
          <RefCode>{facts.reference}</RefCode>
        </Fact>
      )}
      {facts.who && <Fact label="Booked by">{facts.who}</Fact>}
      {facts.whenIso && (
        <Fact label="When">{formatLedgerWhenFull(facts.whenIso)}</Fact>
      )}
      {amount != null && <Fact label="Value">{formatCurrency(amount)}</Fact>}
      {target != null && <Fact label="Target">{formatCurrency(target)}</Fact>}
      {percent != null && percent < 100 && (
        <Fact label="Booked">{percent}%</Fact>
      )}
    </LedgerHoverCard>
  );
}
